import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { SearchBar } from '@/components/ui/SearchBar';
import { ClubCard } from '@/components/club/ClubCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { useClubStore } from '@/stores/clubStore';

export default function ClubsPage() {
  const navigate = useNavigate();
  const colors = useTheme();
  const { isLoading, error, searchQuery, fetchClubs, setSearchQuery, getFilteredClubs } = useClubStore();
  const filteredClubs = getFilteredClubs();

  useEffect(() => { fetchClubs(); }, []);

  const handleRefresh = useCallback(() => { fetchClubs(true); }, [fetchClubs]);

  const renderSkeleton = () => (
    <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="animate-pulse" style={{ display: 'flex', alignItems: 'center', gap: 16, borderRadius: 16, padding: 16, border: `1px solid ${colors.border}`, backgroundColor: colors.surface }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, backgroundColor: colors.background }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ width: 128, height: 16, borderRadius: 8, backgroundColor: colors.background }} />
            <div style={{ width: 80, height: 12, borderRadius: 6, backgroundColor: colors.background }} />
          </div>
          <div style={{ width: 80, height: 36, borderRadius: 18, backgroundColor: colors.background }} />
        </div>
      ))}
    </div>
  );

  if (error && filteredClubs.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, backgroundColor: colors.background }}>
        <ClubsHeader colors={colors} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onRefresh={handleRefresh} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <Icon name="cloud-offline" size={48} color={Colors.primary} />
          <div style={{ fontSize: 14, fontWeight: 500, marginTop: 12, marginBottom: 16, color: colors.text }}>Impossible de charger les clubs</div>
          <button onClick={() => fetchClubs(true)} style={{ backgroundColor: Colors.primary, paddingLeft: 24, paddingRight: 24, paddingTop: 12, paddingBottom: 12, borderRadius: 24, cursor: 'pointer' }}>
            <span style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>Réessayer</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
      <ClubsHeader colors={colors} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onRefresh={handleRefresh} />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px', paddingBottom: 100 }}>
        {filteredClubs.length > 0 ? (
          filteredClubs.map((club, i) => (
            <ClubCard key={club.idclub ? `${club.idclub}-${i}` : `club-${i}`} club={club} onPress={() => navigate(`/club/${club.idclub}`)} />
          ))
        ) : isLoading ? (
          renderSkeleton()
        ) : (
          <EmptyState icon="shield" title={searchQuery ? `Aucun club trouvé pour "${searchQuery}"` : 'Aucun club trouvé'} />
        )}
      </div>
    </div>
  );
}

function ClubsHeader({ colors, searchQuery, setSearchQuery, onRefresh }: any) {
  return (
    <div style={{ padding: '8px 16px 8px 16px', borderBottom: `1px solid ${Colors.primary}15`, backgroundColor: colors.background }}>
      <div style={headerStyles.searchRow}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchBar value={searchQuery} onChangeText={setSearchQuery} placeholder="Rechercher des clubs en Ile-de-France" />
        </div>
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onRefresh}
            aria-label="Actualiser"
            style={{ ...headerStyles.iconButton, flexShrink: 0, backgroundColor: colors.surface, border: 'none', cursor: 'pointer' }}
          >
            <Icon name="refresh" size={22} color={colors.text} />
          </button>
        </div>
      </div>
    </div>
  );
}

const headerStyles: Record<string, React.CSSProperties> = {
  searchRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    paddingTop: 4,
    paddingBottom: 4,
  },
  iconButton: { width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
